import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Define interfaces
interface Stock {
  id: number;
  slug?: string;
  symbol: string;
  name: string;
  current_price?: number;
  currency: string;
  exchange: string;
  instrument_type: string;
  exchange_name?: string;
  currency_name?: string;
  instrument_type_name?: string;
  description?: string;
  sector?: string;
  industry?: string;
  country?: string;
  market_cap?: number;
  pe_ratio?: number | string;
  dividend_yield?: number;
  dividend_per_share?: number;
  eps?: number | string;
  book_value?: number | string;
  profit_margin?: number | string;
  week_52_high?: number;
  week_52_low?: number;
  beta?: number | string;
  analyst_target_price?: number;
  analyst_rating_buy?: number;
  analyst_rating_hold?: number;
  analyst_rating_sell?: number;
}

interface PriceRecord {
  id: number;
  close?: number | null;
  date: string;
  volume?: number;
  formatted_date: string;
  open?: number;
  high?: number;
  low?: number;
}

interface StockDetailProps {
  stock: Stock;
  priceHistory?: PriceRecord[];
  selectedRange?: string;
  csrfToken?: string;
}

const StockDetail: React.FC<StockDetailProps> = ({
  stock,
  priceHistory = [],
  selectedRange = '1y',
  csrfToken
}) => {
  const [chartData, setChartData] = useState(priceHistory.map(record => ({
    date: record.formatted_date,
    price: record.close,
    volume: record.volume,
    open: record.open,
    high: record.high,
    low: record.low
  })).reverse());
  const [currentRange, setCurrentRange] = useState(selectedRange);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch price history when range changes
  useEffect(() => {
    const fetchPriceHistory = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const cacheBuster = `cache_buster=${Date.now()}`;
        const url = `/stocks/${stock.slug || stock.id}.json?range=${currentRange}&${cacheBuster}`;
        const response = await fetch(url, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken || ''
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        const newChartData = data.price_history.map((record: PriceRecord) => ({
          date: record.formatted_date,
          price: record.close,
          volume: record.volume,
          open: record.open,
          high: record.high,
          low: record.low
        })).reverse();
        console.log(`Chart Data for ${currentRange}:`, newChartData);
        setChartData(newChartData);
      } catch (error) {
        console.error('Error fetching price history:', error);
        setError('Failed to load price history. Showing default data.');
        setChartData(priceHistory.map(record => ({
          date: record.formatted_date,
          price: record.close,
          volume: record.volume,
          open: record.open,
          high: record.high,
          low: record.low
        })).reverse());
      } finally {
        setIsLoading(false);
      }
    };

    fetchPriceHistory();
  }, [currentRange, stock.id, stock.slug, priceHistory, csrfToken]);

  // Format price for display
  const formatPrice = (price?: number | string | null): string => {
    if (price === null || price === undefined || price === '') {
      return 'N/A';
    }
    const num = typeof price === 'string' ? parseFloat(price) : Number(price);
    if (isNaN(num)) {
      return 'N/A';
    }
    return `$${num.toFixed(2)}`;
  };

  // Format large numbers (market cap, etc.)
  const formatLargeNumber = (num?: number): string => {
    if (!num) return 'N/A';
    if (num >= 1000000000000) {
      return `$${(num / 1000000000000).toFixed(1)}T`;
    } else if (num >= 1000000000) {
      return `$${(num / 1000000000).toFixed(1)}B`;
    } else if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    }
    return `$${num.toFixed(0)}`;
  };

  // Format percentage
  const formatPercentage = (value?: number): string => {
    if (value === null || value === undefined) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };

  // Format ratio
  const formatRatio = (value?: number | string): string => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return 'N/A';
    return num.toFixed(2);
  };

  // Calculate price change from 52-week low/high
  const getCurrentPrice = () => {
    return stock.current_price || (chartData.length > 0 ? chartData[chartData.length - 1].price : null);
  };

  const currentPrice = getCurrentPrice();
  
  // Calculate 52-week performance
  const calculate52WeekPerformance = () => {
    if (!currentPrice || !stock.week_52_low || !stock.week_52_high) return null;
    const range = stock.week_52_high - stock.week_52_low;
    const position = currentPrice - stock.week_52_low;
    const percentage = (position / range) * 100;
    return {
      percentage: percentage.toFixed(1),
      isHigh: percentage > 80,
      isLow: percentage < 20
    };
  };

  const weekPerformance = calculate52WeekPerformance();

  // Calculate analyst sentiment
  const getAnalystSentiment = () => {
    const buy = stock.analyst_rating_buy || 0;
    const hold = stock.analyst_rating_hold || 0;
    const sell = stock.analyst_rating_sell || 0;
    const total = buy + hold + sell;
    if (total === 0) return null;
    const buyPercentage = (buy / total) * 100;
    if (buyPercentage >= 60) return { rating: 'Strong Buy', color: '#16a34a' };
    if (buyPercentage >= 40) return { rating: 'Buy', color: '#22c55e' };
    if (buyPercentage >= 20) return { rating: 'Hold', color: '#eab308' };
    return { rating: 'Sell', color: '#dc2626' };
  };

  const analystSentiment = getAnalystSentiment();

  // Range selector options
  const rangeOptions = [
    { value: '1m', label: '1 Month' },
    { value: '3m', label: '3 Months' },
    { value: '6m', label: '6 Months' },
    { value: '1y', label: '1 Year' }
  ];

  return (
    <div className="stock-detail">
      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      <div className="stock-header">
        <div className="stock-title">
          <h1>{stock.symbol}</h1>
          <h2 className="stock-company-name">{stock.name}</h2>
          {stock.sector && (
            <p className="stock-sector">{stock.sector} • {stock.industry}</p>
          )}
        </div>
        <div className="stock-current-price">
          <span className="price-label">Current Price</span>
          <span className="price-value">
            {formatPrice(currentPrice)}
          </span>
          {stock.analyst_target_price && (
            <span className="target-price">
              Target: {formatPrice(stock.analyst_target_price)}
            </span>
          )}
        </div>
      </div>
      <div className="key-metrics-section">
        <h3>Key Metrics</h3>
        <div className="metrics-grid">
          <div className="metric-card">
            <span className="metric-label">Market Cap</span>
            <span className="metric-value">{formatLargeNumber(stock.market_cap)}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">P/E Ratio</span>
            <span className="metric-value">{formatRatio(stock.pe_ratio)}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">Dividend Yield</span>
            <span className="metric-value">{formatPercentage(stock.dividend_yield)}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">EPS</span>
            <span className="metric-value">{formatPrice(stock.eps)}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">Beta</span>
            <span className="metric-value">{formatRatio(stock.beta)}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">Profit Margin</span>
            <span className="metric-value">{formatPercentage(stock.profit_margin)}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">52-Week Low</span>
            <span className="metric-value">{formatPrice(stock.week_52_low)}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">52-Week High</span>
            <span className="metric-value">{formatPrice(stock.week_52_high)}</span>
          </div>
        </div>
      </div>
      {stock.week_52_low && stock.week_52_high && (
        <div className="week-range-section">
          <h3>52-Week Range</h3>
          <div className="range-container">
            <div className="range-values">
              <span className="range-low">{formatPrice(stock.week_52_low)}</span>
              <span className="range-high">{formatPrice(stock.week_52_high)}</span>
            </div>
            <div className="range-bar">
              <div className="range-track"></div>
              {weekPerformance && currentPrice && (
                <div 
                  className="range-indicator"
                  style={{ 
                    left: `${weekPerformance.percentage}%`,
                    backgroundColor: weekPerformance.isHigh ? '#16a34a' : weekPerformance.isLow ? '#dc2626' : '#3b82f6'
                  }}
                ></div>
              )}
            </div>
            {weekPerformance && (
              <p className="range-position">
                Current price is {weekPerformance.percentage}% of 52-week range
              </p>
            )}
          </div>
        </div>
      )}
      {analystSentiment && (
        <div className="analyst-section">
          <h3>Analyst Consensus</h3>
          <div className="analyst-rating">
            <span 
              className="rating-badge"
              style={{ backgroundColor: analystSentiment.color }}
            >
              {analystSentiment.rating}
            </span>
            <div className="rating-breakdown">
              <span>Buy: {stock.analyst_rating_buy || 0}</span>
              <span>Hold: {stock.analyst_rating_hold || 0}</span>
              <span>Sell: {stock.analyst_rating_sell || 0}</span>
            </div>
          </div>
        </div>
      )}
      {stock.dividend_yield && stock.dividend_yield > 0 && (
        <div className="dividend-section">
          <h3>Dividend Information</h3>
          <div className="dividend-grid">
            <div className="dividend-card">
              <span className="dividend-label">Annual Yield</span>
              <span className="dividend-value">{formatPercentage(stock.dividend_yield)}</span>
            </div>
            {stock.dividend_per_share && (
              <div className="dividend-card">
                <span className="dividend-label">Dividend per Share</span>
                <span className="dividend-value">{formatPrice(stock.dividend_per_share)}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {chartData.length > 0 && (
        <div className="price-chart-section">
          <div className="chart-header">
            <h3>Price History</h3>
            <div className="range-selector">
              {rangeOptions.map(option => (
                <button
                  key={option.value}
                  className={`range-button ${currentRange === option.value ? 'active' : ''}`}
                  onClick={() => setCurrentRange(option.value)}
                  disabled={isLoading}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="chart-container" style={{ width: '100%', height: '400px' }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                Loading...
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    stroke="#6b7280"
                  />
                  <YAxis 
                    domain={['dataMin - 5', 'dataMax + 5']}
                    tick={{ fontSize: 12 }}
                    stroke="#6b7280"
                  />
                  <Tooltip 
                    formatter={(value: number) => [formatPrice(value), 'Price']}
                    labelFormatter={(label: string) => `Date: ${label}`}
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="price" 
                    stroke="#2563eb" 
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 6, stroke: '#2563eb', strokeWidth: 2, fill: '#ffffff' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StockDetail;